import { ReactElement, ReactNode, useRef } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/_atoms/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_atoms/table";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useThresholdRowVirtualizer } from "@/domain/municipal-property/hooks/use-threshold-row-virtualizer";
import { MunicipalPropertyGhgConflictPreview } from "@/domain/municipal-property/lib/read-municipal-property-ghg-conflict";
import {
  fromJoules,
  GHG_ADMINISTRATION_METHODOLOGY_LOOKUP_MAP as lookup,
  MUNICIPAL_PROPERTY_VALUE_CELL_POLICY,
} from "@ark/shared";

import { MunicipalPropertyTableSpacerRow } from "./municipal-property-table-spacer-row";

interface Props {
  preview?: MunicipalPropertyGhgConflictPreview;
  description?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}
const COLUMN_COUNT: number = 4;

export function MunicipalPropertyGhgConflictDialog({ preview, description, onConfirm, onCancel }: Props): ReactElement {
  const { t } = useDomainTranslation("municipal-property");
  return (
    <AlertDialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <AlertDialogContent className="flex max-h-[85dvh] w-[calc(100%-2rem)] max-w-4xl flex-col overflow-hidden">
        <AlertDialogHeader className="shrink-0">
          <AlertDialogTitle>{t("register.property.overwrite.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {description ?? t("register.property.overwrite.description", { count: preview?.conflictList.length ?? 0 })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {preview && <MunicipalPropertyGhgConflictTable preview={preview} />}
        {preview && !preview.canOverwrite && <p role="alert">{t("register.property.overwrite.permissionDenied")}</p>}
        <AlertDialogFooter className="shrink-0">
          <AlertDialogCancel>{t("register.property.overwrite.cancel")}</AlertDialogCancel>
          <AlertDialogAction disabled={preview ? !preview.canOverwrite : false} onClick={onConfirm}>
            {t("register.property.overwrite.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function MunicipalPropertyGhgConflictTable({
  preview,
}: {
  preview: Pick<MunicipalPropertyGhgConflictPreview, "conflictList">;
}): ReactElement {
  const { t, i18n } = useDomainTranslation("municipal-property");
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const { tableProps, headerRowProps, visibleRowList, topSpacerHeight, bottomSpacerHeight } =
    useThresholdRowVirtualizer({
      rowList: preview.conflictList,
      totalCount: preview.conflictList.length,
      scrollElementRef,
    });
  function formatAmount({ amount, isWater }: { amount: number; isWater: boolean }): string {
    const unit = t(isWater ? "register.property.cubicMeters" : "register.property.mwh");
    return `${(isWater ? amount : fromJoules(amount, "MWh")).toLocaleString(i18n.language)} ${unit}`;
  }
  return (
    <div
      ref={scrollElementRef}
      role="region"
      aria-label={t("register.property.overwrite.conflictList")}
      tabIndex={0}
      className="min-h-0 overflow-auto rounded-md border"
    >
      <Table containerClassName="overflow-visible" {...tableProps}>
        <TableHeader className="sticky top-0 bg-background">
          <TableRow {...headerRowProps}>
            <TableHead>{t("register.property.overwrite.inventory")}</TableHead>
            <TableHead>{t("register.property.overwrite.position")}</TableHead>
            <TableHead>{t("register.property.overwrite.previous")}</TableHead>
            <TableHead>{t("register.property.overwrite.projected")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <MunicipalPropertyTableSpacerRow height={topSpacerHeight} columnCount={COLUMN_COUNT} />
          {visibleRowList.map(({ row, rowProps }) => {
            const isWater = row.sectorId === lookup.sectors.WATER_CONSUMPTION.id;
            const sector = isWater ? lookup.sectors.WATER_CONSUMPTION : lookup.sectors.MUNICIPAL_FACILITIES;
            const sourceEntry = Object.entries(sector.energySources).find(
              ([, source]) => source.id === row.energySourceId,
            );
            const sourceCell = MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.cellList.find(
              (cell) => cell.energySourceKey === sourceEntry?.[0],
            );
            const category = isWater
              ? t("register.property.water")
              : row.subsectorId === lookup.sectors.MUNICIPAL_FACILITIES.subsectors.SCHOOLS_DAYCARE.id
                ? t("register.property.overwrite.schools")
                : row.subsectorId === lookup.sectors.MUNICIPAL_FACILITIES.subsectors.ADMINISTRATIVE_BUILDINGS.id
                  ? t("register.property.overwrite.administration")
                  : t("register.property.overwrite.otherBuildings");
            return (
              <TableRow key={row.id} {...rowProps}>
                <TableCell className="break-words">
                  {row.inventoryName} · {row.year}
                </TableCell>
                <TableCell className="break-words">
                  {category} ·{" "}
                  {sourceCell ? t(`register.property.energySource.${sourceCell.energySourceKey}`) : row.energySourceId}
                </TableCell>
                <TableCell>{row.notationKey ?? formatAmount({ amount: row.previousAmount, isWater })}</TableCell>
                <TableCell>{formatAmount({ amount: row.projectedAmount, isWater })}</TableCell>
              </TableRow>
            );
          })}
          <MunicipalPropertyTableSpacerRow height={bottomSpacerHeight} columnCount={COLUMN_COUNT} />
        </TableBody>
      </Table>
    </div>
  );
}
